import { useCallback, useEffect, useRef, useState } from 'react';
import { baseMime, pickVoiceMime, voiceFileName } from '@functions/voice-recording';
import { VOICE_NOTE_TYPE, canMakeVoiceNote, toVoiceNote } from '@functions/voice-note';

/// Por que a gravação não começou, para a tela explicar do jeito certo.
export type VoiceRecorderError = 'negado' | 'falhou';

const supportedMime = () =>
    typeof window !== 'undefined' && 'MediaRecorder' in window
        ? pickVoiceMime((mime) => MediaRecorder.isTypeSupported(mime))
        : null;

/// Grava nota de voz e entrega em OGG/Opus (formato do WhatsApp); o microfone só abre gravando.
export const useVoiceRecorder = () => {
    const [mime] = useState(supportedMime);
    /// Sem codificador Opus não há nota de voz: o botão some em vez de mandar arquivo recusado.
    const [canConvert, setCanConvert] = useState(false);
    const [recording, setRecording] = useState(false);
    const [seconds, setSeconds] = useState(0);

    const recorderRef = useRef<MediaRecorder | null>(null);
    const streamRef = useRef<MediaStream | null>(null);
    const chunksRef = useRef<Blob[]>([]);
    const startedAtRef = useRef(0);

    useEffect(() => {
        let active = true;
        canMakeVoiceNote().then((ok) => { if (active) setCanConvert(ok); }).catch(() => undefined);
        return () => { active = false; };
    }, []);

    const release = useCallback(() => {
        streamRef.current?.getTracks().forEach((track) => track.stop());
        streamRef.current = null;
        recorderRef.current = null;
        chunksRef.current = [];
        setRecording(false);
        setSeconds(0);
    }, []);

    // O relógio conta pelo horário de início: intervalo atrasa com a aba em segundo plano.
    useEffect(() => {
        if (!recording) return undefined;

        const timer = window.setInterval(
            () => setSeconds((Date.now() - startedAtRef.current) / 1000), 250);

        return () => window.clearInterval(timer);
    }, [recording]);

    const start = useCallback(async (): Promise<VoiceRecorderError | null> => {
        if (!mime || recorderRef.current) return 'falhou';

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            const recorder = new MediaRecorder(stream, { mimeType: mime });

            chunksRef.current = [];
            recorder.ondataavailable = (event) => {
                if (event.data.size > 0) chunksRef.current.push(event.data);
            };

            streamRef.current = stream;
            recorderRef.current = recorder;
            startedAtRef.current = Date.now();

            recorder.start();
            setSeconds(0);
            setRecording(true);

            return null;
        } catch (err) {
            release();
            return err instanceof DOMException && err.name === 'NotAllowedError' ? 'negado' : 'falhou';
        }
    }, [mime, release]);

    /// Para e entrega a nota convertida; nulo sem gravação ou vazia; conversão que falha rejeita.
    const stop = useCallback(async (): Promise<File | null> => {
        const recorder = recorderRef.current;
        if (!recorder || !mime) return null;

        const recorded = await new Promise<Blob>((resolve) => {
            recorder.onstop = () => resolve(new Blob(chunksRef.current, { type: baseMime(mime) }));
            recorder.stop();
        });

        release();

        if (recorded.size === 0) return null;

        const voice = await toVoiceNote(recorded);
        return new File([voice], voiceFileName(new Date()), { type: VOICE_NOTE_TYPE });
    }, [mime, release]);

    const cancel = useCallback(() => {
        const recorder = recorderRef.current;

        if (recorder && recorder.state !== 'inactive') {
            recorder.onstop = null;
            recorder.stop();
        }

        release();
    }, [release]);

    // Sair da tela com o microfone aberto deixaria o indicador do navegador aceso.
    useEffect(() => cancel, [cancel]);

    return { supported: mime !== null && canConvert, recording, seconds, start, stop, cancel };
};
